import { Button, DatePicker, Form, Input, Select, Table, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { newClientToken } from '../../lib/client-token';
import { ApiError } from '../../lib/http';
import { MISSING_REASON, rejectReasonLabel } from '../../lib/status';
import { labsApi, periodsApi, requestsApi, usersApi } from '../../services/resources';
import type { ExperimentRequest, Lab, Period } from '../../types/api';

type StudentOption = { value: string; label: string };

/**
 * 教师代学生提交自主实验申请。
 * 与学生端的差别只有一处：多一个必填的「学生」，提交时带 student_id。
 * 审批流程不变，仍由实验员在学生名下审。
 */
export function TeacherRequestNewPage() {
  const [labs, setLabs] = useState<Lab[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [items, setItems] = useState<ExperimentRequest[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [form] = Form.useForm();

  async function load() {
    const [l, p, r] = await Promise.all([
      labsApi.list({ page: 1, limit: 50 }),
      periodsApi.list(),
      requestsApi.list({ page: 1, limit: 50 }),
    ]);
    setLabs(l.items);
    setPeriods(p);
    setItems(r.items);
  }

  useEffect(() => {
    void load().catch(() => message.error('加载失败'));
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  function searchStudent(keyword: string) {
    if (timer.current) clearTimeout(timer.current);
    const kw = keyword.trim();
    if (kw.length < 2) {
      setStudents([]);
      return;
    }
    // 打字期间不逐字符打接口，250ms 静默后再查
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await usersApi.searchStudents(kw);
        setStudents(rows.map((s) => ({ value: s.id, label: `${s.principal} ${s.name}` })));
      } catch {
        setStudents([]);
      } finally {
        setSearching(false);
      }
    }, 250);
  }

  return (
    <div className="stack">
      <h1 className="page-title">代学生提交实验申请</h1>
      <Form
        form={form}
        layout="vertical"
        onFinish={async (v) => {
          try {
            await requestsApi.create({
              student_id: v.student_id,
              lab_id: v.lab_id,
              date: v.date.format('YYYY-MM-DD'),
              period_ids: v.period_ids,
              title: v.title,
              description: v.description,
              client_token: newClientToken(),
            });
            form.resetFields();
            setStudents([]);
            await load();
            message.success('已提交，等待实验员审批');
          } catch (e) {
            message.error(e instanceof ApiError ? e.message : '提交失败');
          }
        }}
      >
        <Form.Item
          name="student_id"
          label="学生"
          rules={[{ required: true, message: '请选择要代提交的学生' }]}
          extra="输入学号或姓名搜索，至少 2 个字符"
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={searchStudent}
            loading={searching}
            options={students}
            placeholder="学号或姓名"
            notFoundContent={null}
          />
        </Form.Item>
        <Form.Item name="title" label="课题" rules={[{ required: true }]}>
          <Input maxLength={120} />
        </Form.Item>
        <Form.Item name="lab_id" label="实验室" rules={[{ required: true }]}>
          <Select
            options={labs.map((l) => ({ value: l.id, label: `${l.room_code} ${l.name}` }))}
          />
        </Form.Item>
        <Form.Item name="date" label="日期" rules={[{ required: true }]}>
          <DatePicker />
        </Form.Item>
        <Form.Item name="period_ids" label="节次" rules={[{ required: true }]}>
          <Select mode="multiple" options={periods.map((p) => ({ value: p.id, label: p.name }))} />
        </Form.Item>
        <Form.Item name="description" label="实验方案摘要">
          <Input.TextArea rows={4} maxLength={2000} showCount />
        </Form.Item>
        <Button type="primary" htmlType="submit" className="touch">
          提交申请
        </Button>
      </Form>

      <h2 className="page-title">全部学生申请</h2>
      {items.length === 0 ? (
        <EmptyState text="还没有学生申请。" />
      ) : (
        <Table
          scroll={{ x: 760 }}
          rowKey="id"
          dataSource={items}
          pagination={false}
          columns={[
            { title: '课题', dataIndex: 'title' },
            {
              title: '状态',
              render: (_, r) => <StatusBadge status={r.status} reasonType={r.reason_type} />,
            },
            {
              title: '说明',
              render: (_, r) => {
                if (r.status === 'rejected') {
                  const label = rejectReasonLabel(r.reject_reason_code ?? r.reason_type);
                  return r.reject_reason_detail
                    ? `${label}：${r.reject_reason_detail}`
                    : label || MISSING_REASON;
                }
                if (r.sla_overdue && r.status === 'pending') {
                  return '已超过 2 工作日，实验员仍会处理';
                }
                return r.due_at;
              },
            },
            {
              title: '代提交',
              render: (_, r) => (r.submitted_by ? '是' : '学生自提'),
            },
          ]}
        />
      )}
    </div>
  );
}
